import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Typography } from '@mui/material';
import { productsNumber } from 'common/constants/values';
import { FC, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import type { CommonPageForm } from '../../../page-builder-v2.types';
import { ProductSelector } from './product-selector';
import { ProductContentSubsection } from './product-subsections';
import { ProductAssetSubsection } from './product-subsections/product-asset-subsection';
import { ProductReviewSubsection } from './product-subsections/product-review-subsection';
import { ProductsList } from './products-list';
import { styles } from './products-section.styles';

interface ProductsSectionProps {
  onProductsReordered?: () => void;
}

export const ProductsSection: FC<ProductsSectionProps> = ({ onProductsReordered }) => {
  const { control } = useFormContext<CommonPageForm>();

  const productFields = useFieldArray({ control, name: 'products' });
  const { fields } = productFields;

  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(true);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setCardsVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const visibleProducts = fields?.filter((p) => p.status !== 'excluded') ?? [];
  const hasProducts = visibleProducts.length > 0;
  const atLimit = visibleProducts.length >= productsNumber;

  useEffect(() => {
    if (hasProducts) {
      if (
        !selectedProductId ||
        !visibleProducts.some((p) => String(p.product_id) === selectedProductId)
      ) {
        setSelectedProductId(String(visibleProducts[0].product_id));
      }
    } else {
      setSelectedProductId(null);
    }
  }, [fields, selectedProductId, hasProducts]);

  const selectedProductIndex = fields?.findIndex(
    (p) => String(p.product_id) === selectedProductId,
  );

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(true);
    setDialogOpen(true);
  };

  return (
    <Accordion disableGutters expanded={expanded} onChange={() => setExpanded(!expanded)} sx={styles.accordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
        <Box sx={styles.accordionHeader}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography fontWeight={600} variant="subtitle1">Products</Typography>
            {visibleProducts.length > 0 && (
              <Chip color="primary" label={visibleProducts.length} size="small" variant="outlined" />
            )}
          </Box>
          {hasProducts && !atLimit && (
            <Button
              onClick={handleAddClick}
              size="small"
              startIcon={<AddIcon />}
              sx={{ mr: 1 }}
              variant="outlined"
            >
              Add Product
            </Button>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={styles.accordionDetails}>
        <Box sx={styles.container}>
          <Box ref={cardsRef}>
            <ProductsList
              dialogOpen={dialogOpen}
              onAddClick={() => setDialogOpen(true)}
              onDialogClose={() => setDialogOpen(false)}
              onProductsReordered={onProductsReordered}
              onSelect={setSelectedProductId}
              productFields={productFields}
              selectedId={selectedProductId}
            />
          </Box>

          {hasProducts && selectedProductId && selectedProductIndex !== -1 && (
            <Box sx={styles.detailsSection}>
              <ProductSelector
                cardsVisible={cardsVisible}
                onSelect={setSelectedProductId}
                products={visibleProducts}
                selectedId={selectedProductId}
              />
              <ProductContentSubsection
                key={selectedProductIndex}
                productIndex={selectedProductIndex!}
              />
              <ProductReviewSubsection currentProductId={selectedProductId} productFields={productFields} />

              {productFields.fields.map((field, productIdx) => {
                if (field.product_id === selectedProductId) {
                  return (
                    <ProductAssetSubsection
                      key={field.id}
                      currentProductId={selectedProductId}
                      productIdx={productIdx}
                    />
                  );
                }
                return null;
              })}
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
